/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import type {
  Context,
  Exception,
  HrTime,
  Link,
  Span as APISpan,
  SpanOptions as APISpanOptions,
  Attributes,
  AttributeValue,
  SpanContext,
  SpanKind,
  SpanStatus,
  TimeInput,
} from '@opentelemetry/api';
import { diag, SpanStatusCode } from '@opentelemetry/api';
import type { InstrumentationScope } from '@opentelemetry/core';
import {
  addHrTimes,
  millisToHrTime,
  hrTime,
  hrTimeDuration,
  isAttributeValue,
  isTimeInput,
  isTimeInputHrTime,
  otperformance,
  sanitizeAttributes,
} from '@opentelemetry/core';
import type { Resource } from '@opentelemetry/resources';
import {
  ATTR_EXCEPTION_MESSAGE,
  ATTR_EXCEPTION_STACKTRACE,
  ATTR_EXCEPTION_TYPE,
} from '@opentelemetry/semantic-conventions';
import type { ReadableSpan } from './export/ReadableSpan';
import { ExceptionEventName } from './enums';
import type { SpanProcessor } from './SpanProcessor';
import type { TimedEvent } from './TimedEvent';
import type { SpanLimits } from './types';
import type { InspectFn, InspectStylizeOptions } from './inspect';
import {
  formatInspect,
  inspectCustom,
  settledResourceAttributes,
} from './inspect';

/**
 * This type provides the properties of @link{ReadableSpan} at the same time
 * of the Span API
 */
export type Span = APISpan & ReadableSpan;

// `root` is omitted because it is consumed by Tracer.startSpan() to strip
// parent context but it has no meaning when constructing a Span directly.
type SpanOptions = Omit<APISpanOptions, 'root'> & {
  resource: Resource;
  scope: InstrumentationScope;
  context: Context;
  spanContext: SpanContext;
  name: string;
  // Required here to override optional `kind` from the API's SpanOptions
  // SpanImpl assigns it unconditionally and ReadableSpan expects it to be set.
  kind: SpanKind;
  parentSpanContext?: SpanContext;
  spanLimits: SpanLimits;
  spanProcessor: SpanProcessor;
  recordEndMetrics?: () => void;
};

/**
 * This class represents a span.
 */
export class SpanImpl implements Span {
  // Below properties are included to implement ReadableSpan for export
  // purposes but are not intended to be written-to directly.
  private readonly _spanContext: SpanContext;
  readonly kind: SpanKind;
  readonly parentSpanContext?: SpanContext;
  readonly attributes: Attributes = {};
  readonly links: Link[] = [];
  readonly events: TimedEvent[] = [];
  readonly startTime: HrTime;
  readonly resource: Resource;
  readonly instrumentationScope: InstrumentationScope;

  private _droppedAttributesCount = 0;
  private _droppedEventsCount: number = 0;
  private _droppedLinksCount: number = 0;
  private _attributesCount: number = 0;

  name: string;
  status: SpanStatus = {
    code: SpanStatusCode.UNSET,
  };
  endTime: HrTime = [0, 0];
  private _ended = false;
  private _duration: HrTime = [-1, -1];
  private readonly _spanProcessor: SpanProcessor;
  private readonly _spanLimits: SpanLimits;
  private readonly _attributeValueLengthLimit: number;
  private readonly _recordEndMetrics?: () => void;

  private readonly _performanceStartTime: number;
  private readonly _performanceOffset: number;
  private readonly _startTimeProvided: boolean;

  /**
   * Constructs a new SpanImpl instance.
   */
  constructor(opts: SpanOptions) {
    const now = Date.now();

    this._spanContext = opts.spanContext;
    this._performanceStartTime = otperformance.now();
    this._performanceOffset =
      now - (this._performanceStartTime + otperformance.timeOrigin);
    this._startTimeProvided = opts.startTime != null;
    this._spanLimits = opts.spanLimits;
    this._attributeValueLengthLimit =
      this._spanLimits.attributeValueLengthLimit ?? 0;
    this._spanProcessor = opts.spanProcessor;

    this.name = opts.name;
    this.parentSpanContext = opts.parentSpanContext;
    this.kind = opts.kind;
    if (opts.links) {
      for (const link of opts.links) {
        this.addLink(link);
      }
    }
    this.startTime = this._getTime(opts.startTime ?? now);
    this.resource = opts.resource;
    this.instrumentationScope = opts.scope;
    this._recordEndMetrics = opts.recordEndMetrics;

    if (opts.attributes != null) {
      this.setAttributes(opts.attributes);
    }

    this._spanProcessor.onStart(this, opts.context);
  }

  spanContext(): SpanContext {
    return this._spanContext;
  }

  setAttribute(key: string, value?: AttributeValue): this;
  setAttribute(key: string, value: unknown): this {
    if (value == null || this._isSpanEnded()) return this;
    if (key.length === 0) {
      diag.warn(`Invalid attribute key: ${key}`);
      return this;
    }
    if (!isAttributeValue(value)) {
      diag.warn(`Invalid attribute value set for key: ${key}`);
      return this;
    }

    const { attributeCountLimit } = this._spanLimits;
    const isNewKey = !Object.prototype.hasOwnProperty.call(
      this.attributes,
      key
    );

    if (
      attributeCountLimit !== undefined &&
      this._attributesCount >= attributeCountLimit &&
      isNewKey
    ) {
      this._droppedAttributesCount++;
      return this;
    }

    this.attributes[key] = this._truncateToSize(value);
    if (isNewKey) {
      this._attributesCount++;
    }
    return this;
  }

  setAttributes(attributes: Attributes): this {
    for (const key in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        this.setAttribute(key, attributes[key]);
      }
    }
    return this;
  }

  /**
   *
   * @param name Span Name
   * @param [attributesOrStartTime] Span attributes or start time
   *     if type is {@type TimeInput} and 3rd param is undefined
   * @param [timeStamp] Specified time stamp for the event
   */
  addEvent(
    name: string,
    attributesOrStartTime?: Attributes | TimeInput,
    timeStamp?: TimeInput
  ): this {
    if (this._isSpanEnded()) return this;

    const { eventCountLimit } = this._spanLimits;

    if (eventCountLimit === 0) {
      diag.warn('No events allowed.');
      this._droppedEventsCount++;
      return this;
    }

    if (
      eventCountLimit !== undefined &&
      this.events.length >= eventCountLimit
    ) {
      if (this._droppedEventsCount === 0) {
        diag.debug('Dropping extra events.');
      }
      this.events.shift();
      this._droppedEventsCount++;
    }

    if (isTimeInput(attributesOrStartTime)) {
      if (!isTimeInput(timeStamp)) {
        timeStamp = attributesOrStartTime;
      }
      attributesOrStartTime = undefined;
    }

    const sanitized = sanitizeAttributes(attributesOrStartTime);
    const { attributePerEventCountLimit } = this._spanLimits;
    const attributes: Attributes = {};
    let droppedAttributesCount = 0;
    let eventAttributesCount = 0;

    for (const attr in sanitized) {
      if (!Object.prototype.hasOwnProperty.call(sanitized, attr)) {
        continue;
      }
      const attrVal = sanitized[attr];
      if (
        attributePerEventCountLimit !== undefined &&
        eventAttributesCount >= attributePerEventCountLimit
      ) {
        droppedAttributesCount++;
        continue;
      }
      attributes[attr] = this._truncateToSize(attrVal!);
      eventAttributesCount++;
    }

    this.events.push({
      name,
      attributes,
      time: this._getTime(timeStamp),
      droppedAttributesCount,
    });
    return this;
  }

  addLink(link: Link): this {
    if (this._isSpanEnded()) return this;

    const { linkCountLimit } = this._spanLimits;

    if (linkCountLimit === 0) {
      this._droppedLinksCount++;
      return this;
    }

    if (linkCountLimit !== undefined && this.links.length >= linkCountLimit) {
      if (this._droppedLinksCount === 0) {
        diag.debug('Dropping extra links.');
      }
      this.links.shift();
      this._droppedLinksCount++;
    }

    const { attributePerLinkCountLimit } = this._spanLimits;
    const sanitized = sanitizeAttributes(link.attributes);
    const attributes: Attributes = {};
    let droppedAttributesCount = 0;
    let linkAttributesCount = 0;

    for (const attr in sanitized) {
      if (!Object.prototype.hasOwnProperty.call(sanitized, attr)) {
        continue;
      }
      const attrVal = sanitized[attr];
      if (
        attributePerLinkCountLimit !== undefined &&
        linkAttributesCount >= attributePerLinkCountLimit
      ) {
        droppedAttributesCount++;
        continue;
      }
      attributes[attr] = this._truncateToSize(attrVal!);
      linkAttributesCount++;
    }

    const processedLink: Link = { context: link.context };
    if (linkAttributesCount > 0) {
      processedLink.attributes = attributes;
    }
    if (droppedAttributesCount > 0) {
      processedLink.droppedAttributesCount = droppedAttributesCount;
    }

    this.links.push(processedLink);
    return this;
  }

  addLinks(links: Link[]): this {
    for (const link of links) {
      this.addLink(link);
    }
    return this;
  }

  setStatus(status: SpanStatus): this {
    if (this._isSpanEnded()) return this;
    if (status.code === SpanStatusCode.UNSET) return this;
    if (this.status.code === SpanStatusCode.OK) return this;

    const newStatus: SpanStatus = { code: status.code };

    // When using try-catch, the caught "error" is of type `any`. When then assigning `any` to `status.message`,
    // TypeScript will not error. While this can happen during use of any API, it is more common on Span#setStatus()
    // as it's likely used in a catch-block. Therefore, we validate if `status.message` is actually a string, null, or
    // undefined to avoid an incorrect type causing issues downstream.
    if (status.code === SpanStatusCode.ERROR) {
      if (typeof status.message === 'string') {
        newStatus.message = status.message;
      } else if (status.message != null) {
        diag.warn(
          `Dropping invalid status.message of type '${typeof status.message}', expected 'string'`
        );
      }
    }

    this.status = newStatus;
    return this;
  }

  updateName(name: string): this {
    if (this._isSpanEnded()) return this;
    this.name = name;
    return this;
  }

  end(endTime?: TimeInput): void {
    if (this._isSpanEnded()) {
      diag.error(
        `${this.name} ${this._spanContext.traceId}-${this._spanContext.spanId} - You can only call end() on a span once.`
      );
      return;
    }
    this.endTime = this._getTime(endTime);
    this._duration = hrTimeDuration(this.startTime, this.endTime);

    if (this._duration[0] < 0) {
      diag.warn(
        'Inconsistent start and end time, startTime > endTime. Setting span duration to 0ms.',
        this.startTime,
        this.endTime
      );
      this.endTime = this.startTime.slice() as HrTime;
      this._duration = [0, 0];
    }

    if (this._droppedEventsCount > 0) {
      diag.warn(
        `Dropped ${this._droppedEventsCount} events because eventCountLimit reached`
      );
    }
    if (this._droppedLinksCount > 0) {
      diag.warn(
        `Dropped ${this._droppedLinksCount} links because linkCountLimit reached`
      );
    }
    if (this._spanProcessor.onEnding) {
      this._spanProcessor.onEnding(this);
    }

    this._recordEndMetrics?.();
    this._ended = true;
    this._spanProcessor.onEnd(this);
  }

  private _getTime(inp?: TimeInput): HrTime {
    if (typeof inp === 'number' && inp <= otperformance.now()) {
      // must be a performance timestamp
      // apply correction and convert to hrtime
      return hrTime(inp + this._performanceOffset);
    }

    if (typeof inp === 'number') {
      return millisToHrTime(inp);
    }

    if (inp instanceof Date) {
      return millisToHrTime(inp.getTime());
    }

    if (isTimeInputHrTime(inp)) {
      return inp;
    }

    if (this._startTimeProvided) {
      // if user provided a time for the start manually
      // we can't use duration to calculate event/end times
      return millisToHrTime(Date.now());
    }

    const msDuration = otperformance.now() - this._performanceStartTime;
    return addHrTimes(this.startTime, millisToHrTime(msDuration));
  }

  isRecording(): boolean {
    return this._ended === false;
  }

  recordException(exception: Exception, time?: TimeInput): void {
    const attributes: Attributes = {};
    if (typeof exception === 'string') {
      attributes[ATTR_EXCEPTION_MESSAGE] = exception;
    } else if (exception) {
      if (exception.code) {
        attributes[ATTR_EXCEPTION_TYPE] = exception.code.toString();
      } else if (exception.name) {
        attributes[ATTR_EXCEPTION_TYPE] = exception.name;
      }
      if (exception.message) {
        attributes[ATTR_EXCEPTION_MESSAGE] = exception.message;
      }
      if (exception.stack) {
        attributes[ATTR_EXCEPTION_STACKTRACE] = exception.stack;
      }
    }

    // these are minimum requirements from spec
    if (attributes[ATTR_EXCEPTION_TYPE] || attributes[ATTR_EXCEPTION_MESSAGE]) {
      this.addEvent(ExceptionEventName, attributes, time);
    } else {
      diag.warn(`Failed to record an exception ${exception}`);
    }
  }

  get duration(): HrTime {
    return this._duration;
  }

  get ended(): boolean {
    return this._ended;
  }

  get droppedAttributesCount(): number {
    return this._droppedAttributesCount;
  }

  get droppedEventsCount(): number {
    return this._droppedEventsCount;
  }

  get droppedLinksCount(): number {
    return this._droppedLinksCount;
  }

  private _isSpanEnded(): boolean {
    if (this._ended) {
      const error = new Error(
        `Operation attempted on ended Span {traceId: ${this._spanContext.traceId}, spanId: ${this._spanContext.spanId}}`
      );

      diag.warn(
        `Cannot execute the operation on ended Span {traceId: ${this._spanContext.traceId}, spanId: ${this._spanContext.spanId}}`,
        error
      );
    }
    return this._ended;
  }

  // Utility function to truncate given value within size
  // for value type of string, will truncate to given limit
  // for type of non-string, will return same value
  private _truncateToLimitUtil(value: string, limit: number): string {
    if (value.length <= limit) {
      return value;
    }
    return value.substring(0, limit);
  }

  /**
   * If the given attribute value is of type string and has more characters than given {@code attributeValueLengthLimit} then
   * return string with truncated to {@code attributeValueLengthLimit} characters
   *
   * If the given attribute value is array of strings then
   * return new array of strings with each element truncated to {@code attributeValueLengthLimit} characters
   *
   * Otherwise return same Attribute {@code value}
   *
   * @param value Attribute value
   * @returns truncated attribute value if required, otherwise same value
   */
  private _truncateToSize(value: AttributeValue): AttributeValue {
    const limit = this._attributeValueLengthLimit;
    // Check limit
    if (limit <= 0) {
      // Negative values are invalid, so do not truncate
      diag.warn(`Attribute value limit must be positive, got ${limit}`);
      return value;
    }

    // String
    if (typeof value === 'string') {
      return this._truncateToLimitUtil(value, limit);
    }

    // Array of strings
    if (Array.isArray(value)) {
      return (value as []).map(val =>
        typeof val === 'string' ? this._truncateToLimitUtil(val, limit) : val
      );
    }

    // Other types, no need to apply value length limit
    return value;
  }

  [inspectCustom](
    depth: number,
    options: InspectStylizeOptions | undefined,
    inspect: InspectFn | undefined
  ): unknown {
    const payload = {
      name: this.name,
      kind: this.kind,
      spanContext: this._spanContext,
      parentSpanContext: this.parentSpanContext,
      status: this.status,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this._duration,
      ended: this._ended,
      attributes: this.attributes,
      events: this.events,
      links: this.links,
      droppedAttributesCount: this._droppedAttributesCount,
      droppedEventsCount: this._droppedEventsCount,
      droppedLinksCount: this._droppedLinksCount,
      instrumentationScope: this.instrumentationScope,
      resource: { attributes: settledResourceAttributes(this.resource) },
    };
    return formatInspect('SpanImpl', payload, depth, options, inspect);
  }
}



// WEBPACK FOOTER //
// ../../src/Span.ts